import React, { memo, useCallback } from 'react';
import { Briefcase, MapPin } from 'lucide-react';

type HomeUserCardProps = {
  user: any;
  avatarGradient: string;
  isOnline: boolean;
  onViewProfile: (userId: string) => void;
};

function HomeUserCardComponent({ user, avatarGradient, isOnline, onViewProfile }: HomeUserCardProps) {
  const handleClick = useCallback(() => {
    onViewProfile(user.id);
  }, [onViewProfile, user.id]);

  return (
    <button
      onClick={handleClick}
      data-home-user-card="true"
      data-user-id={user.id}
      className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-2xl transition-all hover:scale-105 cursor-pointer text-left group border-2 border-transparent hover:border-purple-300 flex flex-col"
      style={{ contentVisibility: 'auto', containIntrinsicSize: '520px' }}
    >
      <div className="relative w-full aspect-[16/9] sm:aspect-[4/3] flex-shrink-0 bg-gradient-to-br from-purple-100 via-pink-100 to-orange-100">
        {user.showcaseImage ? (
          <img
            src={user.showcaseImage}
            alt={user.name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            loading="lazy"
            decoding="async"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          />
        ) : user.profileImage ? (
          <img
            src={user.profileImage}
            alt={user.name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            loading="lazy"
            decoding="async"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          />
        ) : (
          <div className={`absolute inset-0 w-full h-full bg-gradient-to-br ${avatarGradient} flex items-center justify-center`}>
            <span className="text-7xl sm:text-8xl text-white opacity-90">
              {user.name?.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="truncate group-hover:text-purple-600 transition-colors flex-1">
              {user.name}
            </h3>
          </div>
          {(user.location || user.county) && (
            <div className="flex items-center gap-1 text-gray-500 text-sm">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {[user.location, user.county].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
          {isOnline && (
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm mt-1 shadow-md"
              style={{
                background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
              }}
            >
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-sm"></div>
              <span className="text-white text-xs font-medium">Aktiv nå</span>
            </div>
          )}
        </div>

        {user.bio && (
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">{user.bio}</p>
        )}

        <div className="flex flex-wrap gap-1.5 mb-4 max-h-[3.5rem] overflow-hidden">
          {user.skills?.slice(0, 5).map((skill: string, idx: number) => {
            const isMatching = user.matchingSkills?.includes(skill);
            return (
              <span
                key={idx}
                className={`px-2 py-0.5 text-xs rounded-lg border flex-shrink-0 ${
                  isMatching
                    ? 'bg-gradient-to-r from-yellow-50 to-amber-50 text-amber-800 border-amber-300 font-semibold ring-2 ring-amber-200'
                    : 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border-purple-200'
                }`}
              >
                {skill}
              </span>
            );
          })}
          {user.skills?.length > 5 && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full whitespace-nowrap flex-shrink-0">
              +{user.skills.length - 5}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-purple-600 pt-3 border-t border-purple-100">
          <Briefcase className="w-4 h-4" />
          <span>
            {user.portfolioCount > 0
              ? `${user.portfolioCount} prosjekt${user.portfolioCount !== 1 ? 'er' : ''}`
              : 'Nytt talent'}
          </span>
        </div>
      </div>
    </button>
  );
}

function areEqual(prev: HomeUserCardProps, next: HomeUserCardProps) {
  return (
    prev.user === next.user &&
    prev.avatarGradient === next.avatarGradient &&
    prev.isOnline === next.isOnline &&
    prev.onViewProfile === next.onViewProfile
  );
}

export const HomeUserCard = memo(HomeUserCardComponent, areEqual);
